/**
 * Checkout VIP — flujo de membresía Stripe.
 *
 * El checkout siempre pasa por /api/create-subscription-checkout que:
 *  1. Busca el stripe_price_id pre-creado en membership_products por plan_tier="vip"
 *  2. Usa ese Price ID fijo (precio canónico de Stripe, configurable en el dashboard)
 *  3. Si hay descuento activo, aplica un cupón Stripe encima del Price ID
 *  4. Solo crea precio inline si no existe Price ID en DB (caso de setup inicial)
 *
 * El `VITE_PREMIUM_USD_VIP_PRICE` es solo para mostrar precio en la UI y como
 * fallback de amount cuando el Price ID todavía no está configurado en DB.
 *
 * Gateways:
 *   AR (preferido)  → /api/mercadopago/create-subscription (preapproval con tracking de email/intent)
 *   AR (fallback 1) → VITE_MP_CHECKOUT_VIP_AR / VITE_MP_CHECKOUT_VIP_DISCOUNT_AR (URL pública MP,
 *                     sin token: garantiza que nunca falla aunque el Access Token de Railway
 *                     no tenga acceso al preapproval_plan_id; perdemos pre-fill de email).
 *   AR (fallback 2) → mismo Stripe que internacional (último recurso)
 *   Resto → /api/create-subscription-checkout (Stripe, plan_tier: "vip", moneda USD o EUR según país)
 *         → cupón PROMO{N} aplicado encima del stripe_price_id pre-creado en DB
 *         → fan fallback si falla la API
 */

import { getPlansForCurrency } from "@/config/bioTemplatePremiumPlans";
import {
  getPremiumInvitePaths,
  getPremiumWebsitePaths,
  PREMIUM_INVITE_FLOW_NAME,
  PREMIUM_WEBSITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { BRAND_FAN_URL, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { detectCountryCode } from "@/lib/countryDetection";
import { getHeroCtaSlackExtra } from "@/lib/heroCtaSlackContext";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { captureCheckoutEmailForRecovery } from "@/lib/captureCheckoutEmailForRecovery";
import { fetchCheckoutJson } from "@/lib/fetchCheckoutApi";
import { trackCheckoutOpenedSlack } from "@/lib/trackCheckoutOpenedSlack";
import {
  flushPostHogBeforeNavigationPostHog,
  getPostHogStripeMetadataPostHog,
  trackStripePayFailurePostHog,
  trackVipCheckoutStartedPostHog,
  trackVipPaymentResultPostHog,
} from "@/lib/posthogAnalytics";
import {
  getDataFastStripeMetadata,
  trackDataFastGoal,
} from "@/lib/datafast";

export type VipMembershipCheckoutGateway =
  | "stripe"        // Stripe vía /api/create-subscription-checkout
  | "mercadopago"   // MercadoPago directo (AR)
  | "fan_fallback"; // Fan subdomain fallback

/**
 * ISO2 para metadata Stripe / query fan. Evita `UNKNOWN`.slice(0,2) → "UN" (inválido).
 */
export function billingCountryIso2(raw: string | undefined | null): string {
  const t = String(raw ?? "")
    .trim()
    .toUpperCase();
  if (/^[A-Z]{2}$/.test(t)) return t;
  return "US";
}

/**
 * País para decidir gateway de membresía (MP-first para AR).
 *
 * El caller suele pasar el país inyectado por el Cloudflare Worker. Si ese valor
 * no es un ISO2 válido (típicamente "UNKNOWN" cuando el Worker no corrió, en SPA
 * navigation o si el config no se inyectó), NO defaulteamos a "US" — eso mandaría
 * al argentino a Stripe. Reintentamos con el detector canónico
 * (`detectCountryCode`: Worker → timezone → idioma), que reconoce AR por la
 * timezone `America/Argentina/*` aunque falte el config del Worker.
 */
function resolveCheckoutCountry(raw: string | undefined | null): string {
  const fromCaller = String(raw ?? "").trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(fromCaller)) return fromCaller;
  const detected = detectCountryCode();
  return billingCountryIso2(detected ?? raw);
}

export function vipMembershipFanFallbackUrl(countryCode: string): string {
  const cc = billingCountryIso2(countryCode);
  const base = `${BRAND_FAN_URL.replace(/\/$/, "")}/subscripcion`;
  return `${base}?plan=vip&country=${encodeURIComponent(cc)}`;
}

/** Locale para precios/labels en `getPlansForCurrency` desde el idioma corto del player Mux. */
export function localeForPlansFromMuxLanguage(
  lang: "en" | "es" | "de" | "fr" | "it" | "pt" | "nl",
): string {
  const map: Record<typeof lang, string> = {
    es: "es-AR",
    en: "en-US",
    de: "de-DE",
    fr: "fr-FR",
    it: "it-IT",
    pt: "pt-BR",
    nl: "nl-NL",
  };
  return map[lang] ?? "en-US";
}

export async function startVipMembershipVideoCheckout(options: {
  countryCode: string;
  pathname: string;
  /** Ej. `es-AR` desde Bio; o `localeForPlansFromMuxLanguage` desde Mux. */
  languageForPlans: string;
  /**
   * Tasa de descuento a aplicar (0.10 = 10% off).
   * Se traduce a `discountPercentage` en la API, que aplica un cupón Stripe
   * encima del stripe_price_id pre-creado. No modifica el Price ID.
   * Default: 0 (sin descuento).
   */
  discountRate?: number;
  /**
   * Email del usuario (opcional). Si se provee y el país es AR (MercadoPago),
   * se captura antes de la redirección para habilitar la recuperación de
   * carritos abandonados. No bloquea ni interrumpe el checkout.
   */
  email?: string;
  /**
   * A/B price variant para MercadoPago (matches mercadopago_plans.variant_label).
   * Lo seteamos cuando admin override per-geo lo decide vía `?v=` en la URL,
   * o desde DualCTAVideoLanding cuando la variante A/B activa tiene
   * mp_plan_variant_label asignado.
   * Sin variant → getPlanId usa is_default=true (comportamiento histórico).
   */
  variant?: string;
  /**
   * Stripe Price ID (price_xxx) explícito de la variante A/B activa.
   * Pisa el lookup de membership_products en la API. Solo aplica al branch
   * Stripe (no-AR o AR fallback). Si formato inválido, la API lo ignora.
   */
  stripePriceIdOverride?: string;
  /**
   * Stripe Promotion Code (promo_xxx) — aplica descuento pre-configurado en
   * Stripe Dashboard. Gana sobre discountRate / PROMO{N} dinámico.
   */
  stripePromotionCodeId?: string;
  /**
   * Precio USD de la variante A/B para usar como `amount` cuando NO hay
   * `stripePriceIdOverride` (ej. variante prueba precio sin crear Stripe Price
   * pre-creado). La API crea inline price_data con este amount.
   * Si está set, gana sobre el env price del config local.
   */
  variantPriceUsd?: number;
  /**
   * Atribución A/B para Stripe metadata (queda en payment_intent + subscription
   * para post-conversion analytics). Recolectados directo del worker en
   * __TEMPLATE_CONFIG__.dualVariant + __TEMPLATE_CONFIG__.abBucket.
   */
  dualVariantId?: string;
  dualVariantGeoScope?: string;
  dualVariantGeoCode?: string;
  dualAbBucket?: string;
  /** Template activo (p. ej. USExclusiveLanding) — override del worker config. */
  workerTemplate?: string;
  /**
   * Override del Stripe `success_url` path (raro). Por defecto el cliente usa
   * `getPostPaymentSuccessPath()` → `/portal/complete-profile`; la API
   * `create-subscription-checkout` reescribe igual para plan_tier vip.
   * No afecta MercadoPago AR (su `back_url` viene del dashboard).
   */
  overrideSuccessPath?: string;
  /** Nombre en línea Stripe (ej. "Exclusive Membership" del contrato intl). */
  productNameOverride?: string;
  onIntent?: (detail: {
    gateway: VipMembershipCheckoutGateway;
    /** Código/mensaje Stripe o cliente cuando gateway=fan_fallback */
    error?: string;
    code?: string;
  }) => void;
}): Promise<{ gateway: VipMembershipCheckoutGateway }> {
  const country = resolveCheckoutCountry(options.countryCode);
  const discountRate = options.discountRate ?? 0;

  // ── Argentina: retirado de MercadoPago ───────────────────────────────────
  // AR ahora paga con el MISMO checkout Stripe integrado que el resto (branch de
  // abajo, USD). El kill-switch real es el guard backend `AR_PAYMENTS_ENABLED`:
  // con el flag apagado, /api/create-subscription-checkout responde 403 y el
  // flujo cae al fan_fallback (misma UX que el bloqueo previo); con el flag
  // encendido, AR paga por Stripe. Los endpoints /api/mercadopago/* siguen
  // deprecados. `discountRate` se sigue aplicando en el branch Stripe de abajo.

  const { vipPlan } = getPlansForCurrency("USD", options.languageForPlans);

  // ── Plan deshabilitado → fan fallback ────────────────────────────────────
  if (vipPlan.isDisabled) {
    options.onIntent?.({
      gateway: "fan_fallback",
      error: "vip_plan_disabled",
      code: "VIP_PLAN_DISABLED",
    });
    window.location.assign(vipMembershipFanFallbackUrl(country));
    return { gateway: "fan_fallback" };
  }

  // ── Stripe: checkout de membresía vía API ─────────────────────────────────
  // La API busca el stripe_price_id pre-creado en membership_products (plan_tier="vip").
  // El `amount` es fallback si no hay Price ID en DB todavía.
  // El `discountPercentage` aplica un cupón Stripe encima del Price ID sin cambiarlo.
  const onInvitePage = options.pathname.includes("/invitacion-premium");
  const flowName = onInvitePage ? PREMIUM_INVITE_FLOW_NAME : PREMIUM_WEBSITE_FLOW_NAME;
  const paths = onInvitePage
    ? getPremiumInvitePaths("USD")
    : getPremiumWebsitePaths("USD");
  const origin = window.location.origin;
  const overrideSuccessRaw = options.overrideSuccessPath?.trim();
  const successUrl = overrideSuccessRaw
    ? `${origin}${overrideSuccessRaw.startsWith("/") ? overrideSuccessRaw : `/${overrideSuccessRaw}`}`
    : `${origin}${paths.successPath}`;
  const cancelRaw = paths.cancelPath ?? "/";
  const cancelUrl = `${origin}${cancelRaw.startsWith("/") ? cancelRaw : `/${cancelRaw}`}`;
  const flow_trace_id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `vip_mux_${Date.now()}`;

  // amount en centavos — variant.priceUsd gana cuando está set y no hay
  // stripe_price_id (inline price_data en backend). Sino el Price ID de Stripe
  // pre-creado ya tiene el precio canónico — esto es fallback.
  const effectivePriceUsd =
    typeof options.variantPriceUsd === "number" && options.variantPriceUsd > 0
      ? options.variantPriceUsd
      : vipPlan.price;
  const amountCents = Math.round(effectivePriceUsd * 100);

  // discountPercentage: entero 0-100. La API aplica cupón Stripe si > 0.
  const discountPercentage =
    discountRate > 0 && discountRate < 1
      ? Math.round(discountRate * 100)
      : 0;

  const buyerEmail =
    options.email && options.email.includes("@")
      ? options.email.trim().toLowerCase()
      : "";

  const baseProductName =
    options.productNameOverride?.trim() || vipPlan.productName;
  const productName =
    discountPercentage > 0
      ? `${baseProductName} (${discountPercentage}% OFF)`
      : baseProductName;

  trackVipCheckoutStartedPostHog(
    productName, effectivePriceUsd, "USD", country, "stripe", Boolean(buyerEmail), false, false,
    options.variant,
  );
  trackDataFastGoal("initiate_checkout", {
    flow: flowName,
    plan_tier: "vip",
    currency: "USD",
    amount_usd: String(effectivePriceUsd),
    gateway: "stripe",
  });

  try {
    const stripePriceIdOverride =
      typeof options.stripePriceIdOverride === "string" &&
      /^price_[A-Za-z0-9]+$/.test(options.stripePriceIdOverride.trim())
        ? options.stripePriceIdOverride.trim()
        : undefined;

    const stripePromotionCodeId =
      typeof options.stripePromotionCodeId === "string" &&
      /^promo_[A-Za-z0-9]+$/.test(options.stripePromotionCodeId.trim())
        ? options.stripePromotionCodeId.trim()
        : undefined;

    const heroCtx = getHeroCtaSlackExtra();
    const phMeta = getPostHogStripeMetadataPostHog();
    const datafastMeta = getDataFastStripeMetadata();
    const workerTemplate =
      options.workerTemplate?.trim() ||
      heroCtx.worker_template ||
      getServerTemplateConfig()?.templateName ||
      undefined;
    const languageShort = (options.languageForPlans || "en-US").split("-")[0]?.toLowerCase() || "en";

    type CheckoutSessionResp = {
      url?: string;
      sessionId?: string;
      error?: string;
      code?: string;
      message?: string;
    };

    // Nuevo flow_trace_id en cada POST → idempotency seed distinto (evita colisión
    // Stripe cuando reintentamos sin promo tras coupon_applies_to_nothing).
    let activeFlowTraceId = flow_trace_id;

    const buildCheckoutBody = (promoId: string | undefined, traceId: string) =>
      JSON.stringify({
        amount: amountCents,
        currency: "usd",
        productName,
        buyerEmail,
        country,
        successUrl,
        cancelUrl,
        intervalCount: 1,
        language: languageShort,
        referrer: heroCtx.referrer,
        landingUrl: heroCtx.landingUrl,
        worker_template: workerTemplate,
        hero_cta_type: heroCtx.hero_cta_type,
        hero_cta_label: heroCtx.hero_cta_label,
        hero_cta_url: heroCtx.hero_cta_url,
        geo_summary: heroCtx.geo_summary,
        posthog_session_id: phMeta.ph_session_id,
        posthog_distinct_id: phMeta.ph_distinct_id,
        ...datafastMeta,
        // priceIdOverride: cuando la variante A/B activa tiene stripe_price_id propio,
        // la API lo usa directo (no busca membership_products). Cambia el cobro real.
        ...(stripePriceIdOverride ? { priceIdOverride: stripePriceIdOverride } : {}),
        // Promotion code Stripe (promo_*) — descuento fijo del Dashboard.
        ...(promoId ? { promotionCodeId: promoId } : {}),
        // Descuento: la API aplica cupón Stripe sin modificar el Price ID pre-creado
        ...(discountPercentage > 0 && !promoId
          ? {
              originalAmount: amountCents,
              discountPercentage,
            }
          : {}),
        metadata: {
          source: BRAND_SOURCE_KEY,
          flow: flowName,
          flow_trace_id: traceId,
          pack_id: `${flowName}_vip`,
          plan_tier: "vip",
          product_name: productName,
          billing_interval: "month",
          interval_count: "1",
          // Dual CTA A/B attribution → propaga a metadata Stripe (PI + subscription).
          ...(options.dualVariantId ? { dual_variant_id: options.dualVariantId } : {}),
          ...(options.dualVariantGeoScope ? { dual_variant_geo_scope: options.dualVariantGeoScope } : {}),
          ...(options.dualVariantGeoCode ? { dual_variant_geo_code: options.dualVariantGeoCode } : {}),
          ...(options.dualAbBucket ? { dual_ab_bucket: options.dualAbBucket } : {}),
          ...phMeta,
          ...datafastMeta,
        },
      });

    const postCheckout = (promoId: string | undefined, traceId: string) =>
      fetchCheckoutJson<CheckoutSessionResp>("/api/create-subscription-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: buildCheckoutBody(promoId, traceId),
      });

    let { ok: checkoutOk, status: checkoutStatus, data } = await postCheckout(
      stripePromotionCodeId,
      activeFlowTraceId,
    );

    // Hotfix (cliente): SPA cacheada / promo del contrato + price A/B (us_test2)
    // → coupon_applies_to_nothing o idempotency mismatch. Reintentar sin promo
    // con NUEVO flow_trace_id (key Stripe distinta).
    const shouldRetryWithoutPromo =
      (!checkoutOk || !data.url) &&
      Boolean(stripePromotionCodeId) &&
      (data.code === "coupon_applies_to_nothing" ||
        data.code === "idempotency_error" ||
        /coupon cannot be redeemed|does not apply|idempotent requests can only be used/i.test(
          `${data.error || ""} ${data.message || ""}`,
        ));
    if (shouldRetryWithoutPromo) {
      activeFlowTraceId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${flow_trace_id}_nopromo_${Date.now()}`;
      console.warn(
        "[startVipMembershipVideoCheckout] checkout failed with promo — retry without promo",
        { code: data.code, priceId: stripePriceIdOverride },
      );
      const retry = await postCheckout(undefined, activeFlowTraceId);
      checkoutOk = retry.ok;
      checkoutStatus = retry.status;
      data = retry.data;
    }

    if (!checkoutOk || !data.url) {
      const isBlockedAR = data.code === 'PAYMENTS_BLOCKED_AR';
      if (!isBlockedAR) {
        trackStripePayFailurePostHog({
          stage: "checkout_session_create",
          flow_surface: "vip_membership_video_mux",
          http_status: checkoutStatus,
          stripe_error_code: typeof data.code === "string" ? data.code : undefined,
          error_message: data.error || data.message || "subscription checkout failed",
          amount_cents: amountCents,
          currency: "usd",
          plan_tier: "vip",
          flow_trace_id,
        });
      }
      trackVipPaymentResultPostHog(
        productName, "error", vipPlan.price, "USD", country, "stripe", null,
        data.error || "subscription_checkout_failed", options.variant,
      );
      const errMsg =
        data.message || data.error || "subscription_checkout_failed";
      const errCode =
        typeof data.code === "string" ? data.code : "CHECKOUT_FAILED";
      console.error(
        "[startVipMembershipVideoCheckout] subscription checkout failed:",
        checkoutStatus,
        data,
      );
      if (isBlockedAR) {
        console.warn("[vip checkout] AR payment blocked:", errMsg);
      }
      // onIntent ANTES del redirect — dual emite cta_checkout_error con causa real.
      options.onIntent?.({
        gateway: "fan_fallback",
        error: errMsg,
        code: errCode,
      });
      await flushPostHogBeforeNavigationPostHog();
      window.location.assign(vipMembershipFanFallbackUrl(country));
      return { gateway: "fan_fallback" };
    }
    trackVipPaymentResultPostHog(
      productName, "pending", vipPlan.price, "USD", country, "stripe",
      null, undefined, options.variant,
    );
    // Slack: "abrió el checkout" (Stripe) — keepalive sobrevive al redirect.
    trackCheckoutOpenedSlack({
      gateway: "stripe",
      country,
      template: flowName,
      amountCents,
      currency: "usd",
      planTier: "vip",
      productName,
      flowTraceId: flow_trace_id,
    });
    captureCheckoutEmailForRecovery({
      email: buyerEmail || undefined,
      sessionId: data.sessionId,
      provider: "stripe",
      planTier: "vip",
      flowName,
      country,
      amount: amountCents,
      currency: "usd",
      dualVariantId: options.dualVariantId,
      dualVariantGeoScope: options.dualVariantGeoScope,
      dualVariantGeoCode: options.dualVariantGeoCode,
      dualAbBucket: options.dualAbBucket,
    });
    try {
      const sessionId =
        typeof data.sessionId === "string" ? data.sessionId.trim() : "";
      if (sessionId) {
        localStorage.setItem(
          "premium_last_stripe_session",
          JSON.stringify({
            sessionId,
            flowTraceId: flow_trace_id,
            flowName,
            plan: "vip",
            createdAt: Date.now(),
          }),
        );
      }
    } catch {
      /* non-blocking — recovery también vía cron capture-stripe-emails */
    }
    options.onIntent?.({ gateway: "stripe" });
    // Flush PostHog (vip_checkout_started + dual cta_click_vip / checkout_opened)
    // antes del redirect; sin esto el funnel PH se corta en VIP click.
    await flushPostHogBeforeNavigationPostHog();
    window.location.assign(data.url);
    return { gateway: "stripe" };
  } catch (e) {
    trackStripePayFailurePostHog({
      stage: "checkout_session_create",
      flow_surface: "vip_membership_video_mux",
      failure_kind: "network_or_exception",
      error_message: e instanceof Error ? e.message : String(e),
      amount_cents: amountCents,
      currency: "usd",
      plan_tier: "vip",
      flow_trace_id,
    });
    trackVipPaymentResultPostHog(
      productName, "error", vipPlan.price, "USD", country, "stripe", null,
      e instanceof Error ? e.message : String(e), options.variant,
    );
    console.error("[startVipMembershipVideoCheckout] error:", e);
    options.onIntent?.({
      gateway: "fan_fallback",
      error: e instanceof Error ? e.message : String(e),
      code: "NETWORK_OR_EXCEPTION",
    });
    await flushPostHogBeforeNavigationPostHog();
    window.location.assign(vipMembershipFanFallbackUrl(country));
    return { gateway: "fan_fallback" };
  }
}
